'use client';

import AddIcon from '@mui/icons-material/Add';
import { Alert, AlertColor, Autocomplete, Button, Checkbox, CircularProgress, Grid, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
	DataGridPremium,
	GridColDef,
	GridPaginationModel,
	GridRowSelectionModel,
	GridToolbarColumnsButton,
	GridToolbarContainer,
	GridToolbarDensitySelector,
	GridToolbarFilterButton,
} from '@mui/x-data-grid-premium';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { TypedDocumentNode, useApolloClient, useLazyQuery, useMutation, useQuery } from '@parkchirp/api-client';
import {
	AppNames,
	BulkParkerActionEnum,
	ExceedingAmountDecisionEnum,
	ParkerSuspensionStatusEnum,
	ParkerTerminationStatusEnum,
	TerminationRuleEnum,
} from 'parkchirp-shared/enums';
import React, {
	FC,
	ReactElement,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { CommonContext } from '../../contexts';
import { useDebounce } from '../../hooks/useDebounce';
import {
	ACTIVATE_ALL_PARKERS,
	ACTIVATE_SELECTED_PARKERS,
	ADD_BARCODES_TO_PARKERS_FOR_SUBSCRIPTION,
	CHANGE_PENDING_PARKERS_START_DATE,
	CREATE_LEASES_FIRST_MONTH_INVOICES,
	DELETE_PENDING_PARKERS_BY_SUBSCRIPTION,
	DELETE_PENDING_PARKERS_BY_SUBSCRIPTION_SQS,
	PERFORM_BULK_PARKER_ACTION,
	REMOVE_ACCESS_SYSTEM_META_FOR_SELECTED_PARKERS,
	REMOVE_ACCESS_SYSTEM_META_ON_SUBSCRIPTION_AND_PARKERS,
	SYNC_ALL_PARKERS_IN_ACCESS_SYSTEM,
	SYNC_PARKER_IN_CACHE,
} from '../../mutations';
import {
	GET_FACILITY_V2,
	GET_LEASE_INITIAL_INVOICE_MONTHS,
	GET_PARKER_SKIP_PRORATION_VALUES,
	GET_PARKERS_CSV,
	GET_PARKERS_LPR_READINESS_CSV,
	GET_RATE_BY_ID_V2,
	GET_SUBSCRIPTION_BILLING_PROFILE_DETAILS,
	LIST_ALL_PARKERS_FROM_CACHE,
	LIST_PARKERS_FROM_CACHE,
	LIST_PARKERS_WITH_MONTHLY_FEE_ID,
} from '../../queries';
import type { SetShowDialog, SetShowSnackbar, SnackbarParams } from '../../types';
import {
	Dayjs,
	datagridFilterPanelAttributes,
	dayjs,
	findLabelFromValueAndOptions,
	isAdminDashboard,
	isStartDateInPreviousParkingPeriod,
	shouldShowProrationModalForUpdateTerminationPendingParkers,
} from '../../utils';
import { isOwnerPortal, isTenantPortal } from '../../utils/constants';
import { Snackbar } from '../alerts';
import { DatagridLinearProgress } from '../loading';
import { ConfirmationInputModal, Dialog } from '../modals';
import { BackBillInvoicesModal } from '../modals/backBillInvoicesModal';
import { EditorModal } from '../modals/editorModal';
import RateForm from '../rates/ratesForm';
import { CsvExportButtonV2 } from '../reports';
import LeaseInvoiceMonthsModal from './leaseInvoiceMonthsModal';
import {
	defaultOrderForV2,
	getParkersTableColumns,
	onFilterModelChangeForCache,
	onSortModelChangeForCache,
} from './parkersTableConfig';
import { ParkerTerminationErrorModal } from './parkerTerminationErrorModal';
import { ParkerTerminationModal } from './parkerTerminationModal';

type ParkersTableProps = {
	tabs?: JSX.Element;
	customColumns?: { position: number; column: GridColDef }[]; // position is 0-based
	customFilters?: { field: string; op: string; value: any }[];
	setShowSnackbar?: SetShowSnackbar;
	setShowDialog?: SetShowDialog;
	csvExportButton?: ((props: any) => ReactElement) | null | boolean;
	parkerDetailModal?: ((props: any) => ReactElement) | null;
	tenantSubscriptionId?: string;
	tenantFacilityId?: string;
	showPendingParkersAlert?: boolean;
	refetchSubscriptionData?: () => void;
	theme?: any;
	bulkUploadSubscriptionsAndParkersModal?: ((props: any) => ReactElement) | null;
	showDialog?: ReactNode | null;
	showSnackbar?: Partial<SnackbarParams>;
	initialData?: any;
	initialCount?: number;
	facilityID?: string | null;
	subscriptionID: string | null;
	BulkFeeChangeComponent?: ReactElement;
	BulkRateCSVComponent?: React.ReactNode | ((overrides?: BulkRateOverrides) => JSX.Element);
	showLPRDownload?: boolean;
};
type BulkRateOverrides = {
	query?: TypedDocumentNode;
	order?: { field: string; sort: string };
	filters?: { propertyName: string; op: string; value: string }[];
};

const defaultFiltersForCache: any = [
	{
		propertyName: 'suspensionStatus',
		op: 'isAnyOf',
		value: ['Active', 'Not_Invoiced', 'Suspension_Pending'],
	},
	{
		propertyName: 'terminationStatus',
		op: 'isAnyOf',
		value: ['Active', 'Not_Invoiced', 'Termination_Pending'],
	},
];

export const ParkersTable: FC<ParkersTableProps> = ({
	setShowSnackbar = null,
	setShowDialog = null,
	csvExportButton,
	parkerDetailModal,
	tenantSubscriptionId,
	tenantFacilityId,
	tabs = null,
	customColumns = [],
	customFilters = [],
	showPendingParkersAlert = false,
	refetchSubscriptionData = () => {},
	theme,
	bulkUploadSubscriptionsAndParkersModal = null,
	showDialog = null,
	showSnackbar = null,
	initialData = [],
	initialCount,
	facilityID,
	subscriptionID,
	BulkFeeChangeComponent = null,
	BulkRateCSVComponent = null,
	showLPRDownload = false,
}) => {
	const client = useApolloClient();
	const OwnerPortalTheme = useTheme();

	const subIdToUse = subscriptionID || tenantSubscriptionId;
	const facilityIdToUse = facilityID || tenantFacilityId;

	const [performBulkParkerAction, { loading: performBulkParkerActionLoading }] =
		useMutation(PERFORM_BULK_PARKER_ACTION);
	const [removeAccessSystemMetaForSelectedParkers] = useMutation(REMOVE_ACCESS_SYSTEM_META_FOR_SELECTED_PARKERS);
	const [removeAccessSystemMetaOnSubscriptionAndParkers] = useMutation(
		REMOVE_ACCESS_SYSTEM_META_ON_SUBSCRIPTION_AND_PARKERS,
	);
	const [activateSelectedParkers, { loading: activateSelectedParkersLoading }] =
		useMutation(ACTIVATE_SELECTED_PARKERS);
	const [activateAllParkers, { loading: activateAllParkersLoading }] = useMutation(ACTIVATE_ALL_PARKERS);

	const [
		changePendingParkersStartDateBySubscription,
		{ loading: changePendingParkersStartDateBySubscriptionLoading },
	] = useMutation(CHANGE_PENDING_PARKERS_START_DATE);

	const [createLeasesFirstMonthInvoices] = useMutation(CREATE_LEASES_FIRST_MONTH_INVOICES);
	const [deletePendingParkersBySubscription, { loading: deleteLoading }] = useMutation(
		DELETE_PENDING_PARKERS_BY_SUBSCRIPTION,
	);
	const [deletePendingParkersBySubscriptionSqs, { loading: deletePendingParkersBySubscriptionLoadingSqs }] =
		useMutation(DELETE_PENDING_PARKERS_BY_SUBSCRIPTION_SQS);

	const [{ pageSize, page }, setPaginationModel] = useState<GridPaginationModel>({ pageSize: 100, page: 0 });
	const [bulkParkerActionName, setBulkParkerActionName] = useState<any>(null);
	const [bulkParkerAction, setBulkParkerAction] = useState<string | null>(null);
	const [effectiveDate, setEffectiveDate] = useState<Dayjs | null>(null);
	const [endDate, setEndDate] = useState<Dayjs | null>(null);
	const [isEndDateDisabled, setIsEndDateDisabled] = useState(false);
	const [selectedParkerIds, setSelectedParkerIds] = useState<GridRowSelectionModel>([]);
	const [showSpinner, setShowSpinner] = useState(false);

	const [cacheSortOrder, setCacheSortOrder] = useState(defaultOrderForV2);
	const [cacheFilters, setCacheFilters] = useState(defaultFiltersForCache);
	const debouncedSetCacheFilters = useDebounce(setCacheFilters, 500);
	const [rowCount, setRowCount] = useState<number>(initialCount);
	const [rows, setRows] = useState(initialData);
	const [loading, setLoading] = useState(false);
	const [initialLoadFlag, setInitialLoadFlag] = useState(!!initialData.length);

	const [syncingRowId, setSyncingRowId] = useState<string>('');
	const [syncParkerInCache] = useMutation(SYNC_PARKER_IN_CACHE);
	const [parkersWithErrors, setParkersWithErrors] = useState([]);
	const [newStartDate, setNewStartDate] = useState<Dayjs | null>(null);
	const [showSnackbarForParkersTable, setShowSnackbarForParkersTable = setShowSnackbar] = useState(showSnackbar);
	const [showDialogForParkersTable, setShowDialogForParkersTable = setShowDialog] = useState(showDialog);
	const [syncAllParkers] = useMutation(SYNC_ALL_PARKERS_IN_ACCESS_SYSTEM);

	const { AccessControlledComponents, hasComponentAccess } = useContext(CommonContext);

	const [addBarcodesToParkersForSubscription, { loading: loadingAddBarcodesToParkers }] = useMutation(
		ADD_BARCODES_TO_PARKERS_FOR_SUBSCRIPTION,
		{
			onCompleted: (data) => {
				if (data?.addBarcodesToParkersForSubscription) {
					const message = data?.addBarcodesToParkersForSubscription;
					setShowSnackbar({
						message,
						severity: 'success',
						autoHideDuration: 7000,
					});
				}
			},
			onError: (apolloError) => {
				setShowSnackbar({
					message: apolloError.message,
					severity: 'error',
				});
			},
		},
	);

	const { loading: subscriptionBillingProfileLoading, data: subscriptionBillingProfile } = useQuery(
		GET_SUBSCRIPTION_BILLING_PROFILE_DETAILS,
		{
			variables: {
				subscriptionId: subIdToUse,
			},
			context: {
				apiVersion: 'v2',
				appName: process.env.NEXT_PUBLIC_APP_NAME,
			},
			fetchPolicy: 'network-only',
			skip: !subIdToUse || initialLoadFlag,
		},
	);

	const {
		data: parkersDataFromCache,
		loading: parkerCacheLoading,
		refetch: refetchParkersFromCache,
	} = useQuery(LIST_PARKERS_FROM_CACHE, {
		variables: {
			tenantSubscriptionId,
			limit: pageSize,
			offset: page * pageSize,
			order: cacheSortOrder,
			filters: [
				...cacheFilters,
				...(customFilters.length > 0
					? [
							{
								propertyName: 'subscriptionId',
								op: 'is',
								value: customFilters[0].value,
							},
						]
					: []),
			],
			facilityId: facilityIdToUse,
		},
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		fetchPolicy: 'network-only', // Bypass Apollo's automated caching
		ssr: false,
		skip: initialLoadFlag,
	});

	const { data: facilityData, loading: facilityLoading } = useQuery(GET_FACILITY_V2, {
		variables: {
			facilityId: facilityIdToUse,
			withSublots: false,
			tenantSubscriptionId,
		},
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		fetchPolicy: 'network-only',
		skip: !facilityIdToUse || initialLoadFlag,
	});

	const [getParkerSkipProrationValues] = useLazyQuery(GET_PARKER_SKIP_PRORATION_VALUES, {
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
	});

	const bulkParkerActionOptions = isAdminDashboard
		? useMemo(
				() =>
					Object.keys(BulkParkerActionEnum)
						.map((key) => ({
							value: key,
							label: BulkParkerActionEnum[key as keyof typeof BulkParkerActionEnum],
						}))
						.filter((action) => {
							switch (action.label) {
								case BulkParkerActionEnum.DELETE_PENDING_PARKERS:
									return hasComponentAccess(
										AccessControlledComponents.SubscriptionInfoParkersPageBulkDeletePendingParkersAction,
									);
								case BulkParkerActionEnum.DELETE_ALL_PENDING_PARKERS:
									return hasComponentAccess(
										AccessControlledComponents.SubscriptionInfoParkersPageBulkDeleteAllPendingParkersAction,
									);
								case BulkParkerActionEnum.REMOVE_ACCESS_SYSTEM_META_FOR_SELECTED_PARKERS:
									return hasComponentAccess(
										AccessControlledComponents.SubscriptionInfoParkersPageBulkRemoveAccessSystemMetaForSelectedParkersAction,
									);
								case BulkParkerActionEnum.REMOVE_ACCESS_SYSTEM_META_ON_SUBSCRIPTION_AND_PARKERS:
									return hasComponentAccess(
										AccessControlledComponents.SubscriptionInfoParkersPageBulkRemoveAccessSystemMetaOnSubscriptionAndParkersAction,
									);
								case BulkParkerActionEnum.SUSPEND_PARKERS:
									return hasComponentAccess(
										AccessControlledComponents.SubscriptionInfoParkersPageBulkSuspendParkersAction,
									);
								case BulkParkerActionEnum.UNSUSPEND_PARKERS:
									return hasComponentAccess(
										AccessControlledComponents.SubscriptionInfoParkersPageBulkUnsuspendParkersAction,
									);
								case BulkParkerActionEnum.UPDATE_TERMINATION_PENDING_PARKERS:
									return hasComponentAccess(
										AccessControlledComponents.SubscriptionInfoParkersPageBulkUpdateTerminationPendingParkersAction,
									);
								case BulkParkerActionEnum.UPDATE_SUSPENSION_PENDING_PARKERS:
									return hasComponentAccess(
										AccessControlledComponents.SubscriptionInfoParkersPageBulkUpdateSuspensionPendingParkersAction,
									);
								case BulkParkerActionEnum.CHANGE_PENDING_PARKER_START_DATE:
									return hasComponentAccess(
										AccessControlledComponents.SubscriptionInfoParkersPageBulkChangePendingParkerStartDateAction,
									);
								default:
									return true;
							}
						}),
				[hasComponentAccess],
			)
		: Object.keys(BulkParkerActionEnum).map((key) => ({
				value: key,
				label: BulkParkerActionEnum[key as keyof typeof BulkParkerActionEnum],
			}));

	useEffect(() => {
		if (!loading && parkersDataFromCache) {
			setRows(parkersDataFromCache?.listParkersFromCache?.parkers);
			setRowCount(parkersDataFromCache?.listParkersFromCache?.count);
		}
	}, [loading, parkersDataFromCache]);

	useEffect(() => {
		setRowCount((prevRowCountState) => (rowCount !== undefined ? rowCount : prevRowCountState));
	}, [rowCount]);

	const [getAllParkers, { loading: getAllParkersLoading }] = useLazyQuery(LIST_ALL_PARKERS_FROM_CACHE, {
		variables: {
			tenantSubscriptionId,
			filters: [
				...cacheFilters,
				...(customFilters.length > 0
					? [
							{
								propertyName: 'subscriptionId',
								op: 'is',
								value: customFilters[0].value,
							},
						]
					: []),
			],
			facilityId: facilityIdToUse,
		},
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		fetchPolicy: 'network-only', // Bypass Apollo's automated caching
	});

	const intervalRef = useRef(null);

	const stopInterval = () => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
	};

	useEffect(() => {
		const isUpdatingCheck = () => {
			const parkers = parkersDataFromCache?.listParkersFromCache?.parkers;
			if (parkers) {
				const allUpdated = parkers.every((parker) => parker.isUpdating === false);
				if (allUpdated) {
					stopInterval();
				} else {
					refetchParkersFromCache();
				}
			}
		};
		if (parkersDataFromCache) {
			intervalRef.current = setInterval(isUpdatingCheck, 10000);

			return stopInterval;
		}
	}, [parkersDataFromCache, refetchParkersFromCache]);

	const {
		data: leaseInvoiceMonthsData,
		loading: leaseInvoiceMonthsLoading,
		refetch: refetchInvoiceMonths,
	} = useQuery(GET_LEASE_INITIAL_INVOICE_MONTHS, {
		variables: { subscriptionId: subscriptionID },
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		skip: !subscriptionID || isTenantPortal || isOwnerPortal,
		notifyOnNetworkStatusChange: true,
	});

	const leaseInvoiceMonths: string[] = useMemo(
		() => (leaseInvoiceMonthsLoading ? [] : (leaseInvoiceMonthsData?.getLeaseInitialInvoiceMonths ?? [])),
		[leaseInvoiceMonthsData?.getLeaseInitialInvoiceMonths, leaseInvoiceMonthsLoading],
	);

	useEffect(() => {
		const isLoading =
			subscriptionBillingProfileLoading ||
			performBulkParkerActionLoading ||
			activateSelectedParkersLoading ||
			parkerCacheLoading ||
			deleteLoading ||
			getAllParkersLoading ||
			activateAllParkersLoading ||
			deletePendingParkersBySubscriptionLoadingSqs ||
			changePendingParkersStartDateBySubscriptionLoading;
		setLoading(isLoading);
	}, [
		subscriptionBillingProfileLoading,
		performBulkParkerActionLoading,
		activateSelectedParkersLoading,
		parkerCacheLoading,
		deleteLoading,
		getAllParkersLoading,
		activateAllParkersLoading,
		deletePendingParkersBySubscriptionLoadingSqs,
		changePendingParkersStartDateBySubscriptionLoading,
	]);
	async function getRateForm(rateId: string): Promise<ReactElement> {
		const rateResponse = await client.query({
			query: GET_RATE_BY_ID_V2,
			variables: {
				rateId,
			},
			context: {
				apiVersion: 'v2',
				appName: process.env.NEXT_PUBLIC_APP_NAME,
			},
			fetchPolicy: 'network-only',
		});
		const rate = rateResponse.data.getRateDetailsById;
		return (
			<RateForm
				selectedRate={rate}
				setShowDialog={setShowDialogForParkersTable}
				isChild={rate.parentRateId}
				setShowSnackbar={setShowSnackbarForParkersTable}
				queryVariables={{
					limit: pageSize,
					offset: page * pageSize,
					order: defaultOrderForV2,
					filters: [],
				}}
				refetchParkers={refetchParkersFromCache}
				theme={OwnerPortalTheme || theme}
			/>
		);
	}

	const handleSyncInCache = async (parkerId) => {
		setSyncingRowId(parkerId);
		try {
			await syncParkerInCache({
				variables: {
					parkerId,
				},
				context: {
					apiVersion: 'v2',
					appName: process.env.NEXT_PUBLIC_APP_NAME,
				},
			});
			setShowSnackbarForParkersTable({
				message: 'Successfully Synced Parker In Cache',
				severity: 'success',
				autoHideDuration: 6000,
			});
			await refetchParkersFromCache();
		} catch {
			setShowSnackbarForParkersTable({
				message: 'There was an error syncing this parker in the cache',
				severity: 'error',
				autoHideDuration: 6000,
			});
		} finally {
			setSyncingRowId('');
		}
	};

	// const parkerRows = parkersDataFromCache?.listParkersFromCache?.parkers;

	const columns = getParkersTableColumns({
		parkerDetailModal,
		setShowDialog: setShowDialogForParkersTable,
		tenantSubscriptionId,
		getRateForm,
		syncingRowId,
		handleSyncInCache,
		refetchParkersFromCache,
		theme: theme || OwnerPortalTheme,
		hasComponentAccess,
	});
	// Insert custom columns
	customColumns.forEach(({ position, column }) => columns.splice(position, 0, column));

	const handleActivateOrDeleteForSqs = async (action: any, actionName: string, createBackBillInvoices?: boolean) => {
		let variables: any = {
			subscriptionId: subIdToUse,
			tenantSubscriptionId,
		};
		if (actionName.includes('activate')) {
			variables = {
				...variables,
				createBackBillInvoices,
			};
		}
		const promise = await action({
			context: {
				apiVersion: 'v2',
				appName: process.env.NEXT_PUBLIC_APP_NAME,
			},
			variables,
		});
		return promise;
	};

	const handleActivateOrDelete = async (
		action: any,
		parkerIds: string[],
		actionName: string,
		createBackBillInvoices?: boolean,
	): Promise<{ parkerId: string; errorMessage: string }[]> => {
		let variables: any = {
			subscriptionId: subIdToUse,
			parkerIds,
			tenantSubscriptionId,
		};
		if (actionName.includes('activate')) {
			variables = {
				...variables,
				createBackBillInvoices,
			};
		}
		if (actionName.includes('change')) {
			variables = {
				...variables,
				startDate: newStartDate,
			};
		}
		const promise = action({
			context: {
				apiVersion: 'v2',
			},
			variables,
		});

		const results = await Promise.allSettled([promise]);

		const flatMap = results.flatMap((r: any) => {
			const { parkersWithErrors: errorParkers } = r.value.data[actionName];
			const { errorMessage = '' } = r.value.data[actionName];

			return errorParkers.map((parker: any) => ({
				...parker,
				errorMessage,
			}));
		});

		return flatMap;
	};

	async function handleActionSubmitClick(
		allPendingParkerData = null,
		skipProration = null,
		effectiveDateToUse = effectiveDate,
		exceedingAmountDecision: ExceedingAmountDecisionEnum = null,
		createBackBillInvoices: boolean = false,
	) {
		if (loading) {
			return;
		}
		let maybeFormattedEffectiveDate = null;
		let maybeFormattedEndDate = null;

		if (effectiveDateToUse) {
			maybeFormattedEffectiveDate = dayjs(effectiveDateToUse).format('YYYY-MM-DD');
		}
		if (endDate) {
			maybeFormattedEndDate = dayjs(endDate).format('YYYY-MM-DD');
		}
		let resultMessage = `Bulk action "${bulkParkerActionName}" successful`;
		let severity: AlertColor = 'success';
		let errors = [];
		let skipFinally = false;

		let parkerData = allPendingParkerData;

		if (!parkerData) {
			parkerData = selectedParkerIds;
		}

		try {
			setShowDialogForParkersTable(null);
			setShowSpinner(true);

			if (bulkParkerAction === 'ACTIVATE_PENDING_PARKERS') {
				const { data } = await activateSelectedParkers({
					context: {
						apiVersion: 'v2',
						appName: process.env.NEXT_PUBLIC_APP_NAME,
					},
					variables: {
						parkerIds: selectedParkerIds,
						createBackBillInvoices,
					},
				});
				const { errors: activationErrors } = data.activateSelectedParkers;

				errors = activationErrors.flatMap((activationError) => {
					let { errorMessage } = activationError;
					if (!errorMessage && activationError.parkersAfterLastStartDate?.length) {
						errorMessage = `Some parker's start dates are after the facility's last available billing start date`;
					}

					return [
						...(activationError.failedParkers.map((parkerId) => ({
							parkerId,
							errorMessage,
						})) ?? []),
						...(activationError.parkersAfterLastStartDate?.map((parkerId) => ({
							parkerId,
							errorMessage,
						})) ?? []),
					];
				});

				resultMessage = errors.length
					? 'Some of these parkers could not be activated'
					: 'Successfully activated pending parkers';
				severity = errors.length ? 'warning' : 'success';
			} else if (bulkParkerAction === 'ACTIVATE_ALL_PENDING_PARKERS') {
				await activateAllParkers({
					context: {
						apiVersion: 'v2',
						appName: process.env.NEXT_PUBLIC_APP_NAME,
					},
					variables: {
						subscriptionId: subIdToUse,
						createBackBillInvoices,
					},
				});

				resultMessage = `Started activating all pending parkers please wait for these changes to take place.
                You will receive an email regarding any problematic parkers`;

				severity = 'info';
			} else if (bulkParkerAction === 'DELETE_ALL_PENDING_PARKERS') {
				await handleActivateOrDeleteForSqs(
					deletePendingParkersBySubscriptionSqs,
					'deletePendingParkersBySubscriptionSqs',
				);

				resultMessage = `Started deleting all pending parkers please wait for these changes to take place.
                You will receive an email regarding any problematic parkers`;

				severity = 'info';
			} else if (bulkParkerAction === 'DELETE_PENDING_PARKERS') {
				errors = await handleActivateOrDelete(
					deletePendingParkersBySubscription,
					parkerData,
					'deletePendingParkersBySubscription',
				);

				resultMessage = errors.length
					? 'Some of these parkers could not be deleted'
					: 'Successfully deleted pending parkers';
				severity = errors.length ? 'warning' : 'success';
			} else if (bulkParkerAction === 'CHANGE_PENDING_PARKER_START_DATE') {
				errors = await handleActivateOrDelete(
					changePendingParkersStartDateBySubscription,
					parkerData,
					'changePendingParkersStartDateBySubscription',
				);

				resultMessage = errors.length
					? 'Some of these parkers could not have their start date changed'
					: 'Successfully changed start date for selected pending parkers';
				severity = errors.length ? 'warning' : 'success';
			} else if (bulkParkerAction === 'REMOVE_ACCESS_SYSTEM_META_FOR_SELECTED_PARKERS') {
				await removeAccessSystemMetaForSelectedParkers({
					variables: {
						parkerIds: selectedParkerIds as string[],
						subscriptionId: subIdToUse,
					},
					context: {
						apiVersion: 'v2',
						appName: process.env.NEXT_PUBLIC_APP_NAME,
					},
				});

				resultMessage = 'Successfully removed access system meta from the selected parkers';
				severity = 'success';
			} else if (bulkParkerAction === 'REMOVE_ACCESS_SYSTEM_META_ON_SUBSCRIPTION_AND_PARKERS') {
				await removeAccessSystemMetaOnSubscriptionAndParkers({
					variables: {
						subscriptionId: subIdToUse,
					},
					context: {
						apiVersion: 'v2',
						appName: process.env.NEXT_PUBLIC_APP_NAME,
					},
				});

				resultMessage = 'Successfully removed access system meta from the subscription and all parkers';
				severity = 'success';
			} else {
				setShowSpinner(true);
				await performBulkParkerAction({
					variables: {
						subscription_id: subIdToUse,
						parker_ids: selectedParkerIds,
						action: bulkParkerAction,
						termination_date: maybeFormattedEffectiveDate,
						suspension_start_date: maybeFormattedEffectiveDate,
						suspension_end_date: maybeFormattedEndDate,
						tenantSubscriptionId,
						skipProration: skipProration !== false,
						...(exceedingAmountDecision !== null && { exceedingAmountDecision }),
					},
					context: {
						appName: process.env.NEXT_PUBLIC_APP_NAME,
					},
				});
			}
		} catch (e) {
			if (e.message?.includes('exceeds')) {
				setShowDialogForParkersTable(
					<ParkerTerminationErrorModal
						{...{
							handleClose: () => setShowDialogForParkersTable(null),
							loading,
							theme: theme || OwnerPortalTheme,
							handleSave: (descision: ExceedingAmountDecisionEnum) =>
								handleActionSubmitClick(null, false, effectiveDateToUse, descision, false),
						}}
					/>,
				);
				skipFinally = true; // Skip finally b/c the modal will call this function again
				return;
			}
			resultMessage = `Unable to ${bulkParkerActionName}: ${e.message}`;
			severity = 'error';
		} finally {
			if (!skipFinally) {
				const errorParkerIds = errors.filter((e) => e.parkerId).map((err) => err.parkerId);
				const errorMessage = errors.length > 0 && errors[0].errorMessage ? errors[0].errorMessage : '';
				setSelectedParkerIds([]);
				setBulkParkerAction(null);
				setBulkParkerActionName(null);
				setShowSpinner(false);
				refetchParkersFromCache();
				refetchSubscriptionData();
				refetchInvoiceMonths();
				setParkersWithErrors([...errorParkerIds]);
				if (severity === 'success') {
					resultMessage +=
						'. If the facility has an integration, it may take up to 10 minutes to see the changes reflected in the external access system.';
				}
				setShowSnackbarForParkersTable({
					message: `${resultMessage}${errorMessage ? `: ${errorMessage}` : errorMessage}`,
					severity,
				});
			}
		}
	}

	const triggerShowBackBillModal = async () => {
		let showBackBillModal;
		let allPendingParkerIds;
		if (bulkParkerAction === 'ACTIVATE_ALL_PENDING_PARKERS') {
			const { data } = await getAllParkers();
			allPendingParkerIds = data?.listAllParkersFromCache?.parkers.map(({ id }) => id) || [];
			showBackBillModal = isStartDateInPreviousParkingPeriod(
				data?.listAllParkersFromCache?.parkers,
				allPendingParkerIds,
			);
		} else {
			showBackBillModal = isStartDateInPreviousParkingPeriod(rows, selectedParkerIds);
		}
		if (showBackBillModal) {
			setShowDialogForParkersTable(
				<BackBillInvoicesModal
					facilityIds={[facilityIdToUse]}
					closeModal={() => setShowDialogForParkersTable(null)}
					handleDismiss={async () => {
						await handleActionSubmitClick(allPendingParkerIds, null, null, null, false);
					}}
					handleConfirm={async () => {
						await handleActionSubmitClick(allPendingParkerIds, null, null, null, true);
					}}
					{...(tenantSubscriptionId && { appName: AppNames.TENANT_PORTAL })}
				/>,
			);
			return;
		}
		await handleActionSubmitClick(allPendingParkerIds);
	};

	function isSaveButtonForParkerActionDisabled() {
		// expanded logic for if the suspend/update suspension pending parkers
		// save button should be disabled
		if (!rows?.length || !selectedParkerIds.length) {
			return true;
		}
		if (effectiveDate) {
			if (isEndDateDisabled) {
				return false;
			}
			return !endDate;
		}
		return true;
	}

	async function showTerminationConfirmModal() {
		const billingProfileHasThirtyDayNotice = [
			TerminationRuleEnum.DAILY_PRORATION,
			TerminationRuleEnum.HALF_MONTH,
		].includes(subscriptionBillingProfile?.getSubscription?.billingProfile?.terminationRule);

		if (!billingProfileHasThirtyDayNotice) {
			handleActionSubmitClick();
			return;
		}
		const terminationDate = dayjs(effectiveDate);
		const whenAutoInvoiceIsSentForMonthOfTermination = dayjs(terminationDate)
			.subtract(1, 'month')
			.set('date', subscriptionBillingProfile?.getSubscription?.billingProfile?.billingDay);
		const now = dayjs();
		const newTerminationDateInvoiceHasBeenSent = whenAutoInvoiceIsSentForMonthOfTermination.isBefore(now);

		// if the selected date and previous selected date month's do not match
		// and both selected and previously selected month's invoices have been sent,
		// show manual adjustment prompt
		const selectedParkerExistingTerminationDates = selectedParkerIds
			.map((selectedId) => {
				const row = rows.find(({ id }) => id === selectedId);
				return row?.parkerTerminationDate;
			})
			.filter((maybeDate) => !!maybeDate);

		const someParkersHaveAlreadyBeenProrated = selectedParkerExistingTerminationDates.some(
			(nearestTerminationDate) => {
				const rowTerminationDate = dayjs.utc(nearestTerminationDate);
				const whenAutoInvoiceIsSentForRowTermination = dayjs(rowTerminationDate)
					.subtract(1, 'month')
					.set('date', subscriptionBillingProfile?.getSubscription?.billingProfile?.billingDay);
				return whenAutoInvoiceIsSentForRowTermination.isBefore(now);
			},
		);
		const terminationDateWasMovedBetweenMonths = selectedParkerExistingTerminationDates.some(
			(nearestTerminationDate) => {
				const rowTerminationDate = dayjs.utc(nearestTerminationDate);
				return rowTerminationDate.month() !== terminationDate.month();
			},
		);

		let parkerSkipProrationValues = [];
		if (BulkParkerActionEnum[bulkParkerAction] === BulkParkerActionEnum.UPDATE_TERMINATION_PENDING_PARKERS) {
			const { data } = await getParkerSkipProrationValues({ variables: { parkerIds: selectedParkerIds } });
			parkerSkipProrationValues = data.getParkerSkipProrationValues;
		}

		const allParkersWereOptedOutOfProration = parkerSkipProrationValues.every(({ skipProration }) => skipProration);
		if (
			BulkParkerActionEnum[bulkParkerAction] === BulkParkerActionEnum.TERMINATE_ACTIVE_PARKERS ||
			(BulkParkerActionEnum[bulkParkerAction] === BulkParkerActionEnum.UPDATE_TERMINATION_PENDING_PARKERS &&
				shouldShowProrationModalForUpdateTerminationPendingParkers(
					someParkersHaveAlreadyBeenProrated,
					terminationDateWasMovedBetweenMonths,
					newTerminationDateInvoiceHasBeenSent,
					allParkersWereOptedOutOfProration,
				))
		) {
			setShowDialogForParkersTable(
				<ParkerTerminationModal
					{...{
						handleClose: () => setShowDialogForParkersTable(null),
						loading,
						// for some reason the below function needs the effectiveDate hook as a parameter, otherwise
						// it's unable to access the latest value when the user submits a parker's termination
						handleSave: (skipProration, effectiveDateToUse) =>
							handleActionSubmitClick(null, skipProration, effectiveDateToUse),
						showManualAdjustmentAlert:
							someParkersHaveAlreadyBeenProrated && !allParkersWereOptedOutOfProration,
						effectiveDate,
						setEffectiveDate,
						terminationNoticeDay:
							subscriptionBillingProfile?.getSubscription?.billingProfile?.terminationNoticeDay,
						terminationRule: subscriptionBillingProfile?.getSubscription?.billingProfile?.terminationRule,
						theme: theme || OwnerPortalTheme,
					}}
				/>,
			);
		} else if (BulkParkerActionEnum[bulkParkerAction] === BulkParkerActionEnum.UPDATE_TERMINATION_PENDING_PARKERS) {
			const confirmationModal = (
				<EditorModal
					theme={theme || OwnerPortalTheme}
					loading={false}
					handleClose={() => setShowDialogForParkersTable(null)}
					handleSave={handleActionSubmitClick}
					headerText={`Update ${selectedParkerIds.length} Termination Pending parker(s)?`}
				>
					<p>
						The monthly invoices for these parkers must be manually adjusted to correct the proration
						amount.
					</p>
				</EditorModal>
			);
			setShowDialogForParkersTable(confirmationModal);
		}
	}

	const doCreateLeasesFirstMonthInvoices = useCallback(
		async (months: string[]) => {
			await createLeasesFirstMonthInvoices({
				variables: {
					subscriptionId: subscriptionID,
					months,
				},
				context: {
					apiVersion: 'v2',
					appName: process.env.NEXT_PUBLIC_APP_NAME,
				},
			});

			setShowDialogForParkersTable(null);

			await refetchInvoiceMonths();
		},
		[createLeasesFirstMonthInvoices, refetchInvoiceMonths, setShowDialogForParkersTable, subscriptionID],
	);

	function renderCorrectInputsFromAction() {
		if (showSpinner) {
			return <CircularProgress color='primary' />;
		}
		if (bulkParkerAction === 'ACTIVATE_PENDING_PARKERS') {
			return (
				<Button
					disabled={!rows?.length || !selectedParkerIds.length || loading}
					onClick={triggerShowBackBillModal}
					variant='contained'
					data-cy='activate-parkers-button'
				>
					Activate Parkers
				</Button>
			);
		}
		if (bulkParkerAction === 'ACTIVATE_ALL_PENDING_PARKERS') {
			return (
				<Button
					disabled={!rows?.length || loading}
					onClick={triggerShowBackBillModal}
					variant='contained'
					data-cy='activate-all-parkers-button'
				>
					Activate All Parkers
				</Button>
			);
		}
		if (bulkParkerAction === 'DELETE_PENDING_PARKERS') {
			// Activate Parkers button, disabled until selection made
			return (
				<Button
					disabled={!rows?.length || !selectedParkerIds.length}
					onClick={() => handleActionSubmitClick()}
					variant='contained'
					data-cy='delete-parkers-button'
				>
					Delete Parkers
				</Button>
			);
		}
		if (bulkParkerAction === 'DELETE_ALL_PENDING_PARKERS') {
			// Activate Parkers button, disabled until selection made
			return (
				<Button
					disabled={!rows?.length}
					onClick={() => handleActionSubmitClick()}
					variant='contained'
					data-cy='delete-all-parkers-button'
				>
					Delete All Parkers
				</Button>
			);
		}
		if (
			bulkParkerAction === 'TERMINATE_ACTIVE_PARKERS' ||
			bulkParkerAction === 'UPDATE_TERMINATION_PENDING_PARKERS'
		) {
			let minDate = dayjs();
			// tenants only, not owner/admin
			if (isTenantPortal) {
				const today = dayjs();
				const tenantEnforceTermination =
					subscriptionBillingProfile?.getSubscription?.tenantEnforceTermination !== false;
				const terminationNoticeDay =
					subscriptionBillingProfile?.getSubscription?.billingProfile?.terminationNoticeDay;
				const terminationRule = subscriptionBillingProfile?.getSubscription?.billingProfile?.terminationRule;
				const terminationNoticeDayInt =
					typeof terminationNoticeDay === 'number'
						? terminationNoticeDay
						: parseInt(terminationNoticeDay, 10);
				if (tenantEnforceTermination) {
					// RULE 1: Day of Month Cutoff
					if (terminationRule === TerminationRuleEnum.DAY_OF_MONTH_CUTOFF && terminationNoticeDayInt) {
						// If current day is BEFORE the notice day, min date is the end of the CURRENT month.
						if (today.date() < terminationNoticeDayInt) {
							minDate = today.endOf('month');
						} else {
							// If current day is ON or AFTER the notice day, min date is the end of the NEXT month.
							minDate = today.add(1, 'month').endOf('month');
						}
					}
					// RULE 2: 30 Day Notice
					if (
						[TerminationRuleEnum.DAILY_PRORATION, TerminationRuleEnum.HALF_MONTH].includes(
							terminationRule as TerminationRuleEnum,
						)
					) {
						minDate = today.add(30, 'days');
					}
				}
			}

			return (
				<>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label='Effective Date'
							value={effectiveDate}
							onChange={(newValue: Dayjs | null) => {
								const year = newValue.year();
								if (year < 1900) {
									return; // Don't set the date if the year is invalid
								}
								setEffectiveDate(newValue || null);
							}}
							views={['year', 'month', 'day']}
							format='MM/DD/YYYY'
							minDate={tenantSubscriptionId ? minDate : null}
							slotProps={{
								openPickerIcon: {
									'data-cy': 'date-picker-open-icon',
								},
								textField: {
									inputProps: {
										'data-cy': 'change-parker-start-date-date-picker',
									},
									sx: {
										height: '35px',
										boxSizing: 'border-box',
										'& .MuiInputBase-root': {
											height: '35px',
										},
										'& .MuiInputLabel-root': {
											marginTop: '-10px',
										},
										'& .MuiInputLabel-shrink': {
											marginTop: '0px',
										},
									},
								},
							}}
						/>
					</LocalizationProvider>

					<Button
						disabled={!rows?.length || !effectiveDate || !selectedParkerIds.length}
						onClick={showTerminationConfirmModal}
						variant='contained'
						data-cy='save-button'
						style={{ height: '35px', marginLeft: '10px' }}
					>
						Save
					</Button>
				</>
			);
		}
		if (bulkParkerAction === 'SUSPEND_PARKERS' || bulkParkerAction === 'UPDATE_SUSPENSION_PENDING_PARKERS') {
			// Effective date, end date, end date checkbox, save button
			// disabled until all inputs filled an selection/checkbox set
			return (
				<>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label='Effective Date'
							value={effectiveDate}
							onChange={(newValue: Dayjs | null) => {
								const year = newValue.year();
								if (year < 1900) {
									return; // Don't set the date if the year is invalid
								}
								setEffectiveDate(newValue || null);
							}}
							views={['year', 'month', 'day']}
							format='MM/DD/YYYY'
							slotProps={{
								openPickerIcon: {
									'data-cy': 'date-picker-open-icon',
								},
								textField: {
									inputProps: {
										'data-cy': 'suspend-parker-effective-date-date-picker',
									},
									sx: {
										height: '35px',
										boxSizing: 'border-box',
										'& .MuiInputBase-root': {
											height: '35px',
											marginLeft: '10px',
										},
										'& .MuiInputLabel-root': {
											marginTop: '-10px',
											left: '9px',
										},
										'& .MuiInputLabel-shrink': {
											marginTop: '0px',
										},
									},
								},
							}}
						/>
						<DatePicker
							label='End Date'
							disabled={isEndDateDisabled}
							value={endDate}
							onChange={(newValue: Dayjs | null) => {
								const year = newValue.year();
								if (year < 1900) {
									return; // Don't set the date if the year is invalid
								}
								setEndDate(newValue || null);
							}}
							views={['year', 'month', 'day']}
							format='MM/DD/YYYY'
							slotProps={{
								openPickerIcon: {
									'data-cy': 'date-picker-open-icon',
								},
								textField: {
									inputProps: {
										'data-cy': 'suspend-parker-end-date-date-picker',
									},
									sx: {
										height: '35px',
										boxSizing: 'border-box',
										'& .MuiInputBase-root': {
											height: '35px',
											marginLeft: '10px',
										},
										'& .MuiInputLabel-root': {
											marginTop: '-10px',
											left: '9px',
										},
										'& .MuiInputLabel-shrink': {
											marginTop: '0px',
										},
									},
								},
							}}
						/>
					</LocalizationProvider>
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<Checkbox
							checked={isEndDateDisabled}
							onChange={(event) => {
								if (event.target.checked) {
									setEndDate(null);
								}
								setIsEndDateDisabled(!!event.target.checked);
							}}
							data-cy='no-end-date-checkbox'
							style={{ height: '35px', marginLeft: '10px' }}
						/>
						<span>No End Date</span>
					</div>
					<Button
						disabled={isSaveButtonForParkerActionDisabled()}
						onClick={() => handleActionSubmitClick()}
						variant='contained'
						data-cy='save-button'
						style={{ height: '35px', marginLeft: '10px' }}
					>
						Save
					</Button>
				</>
			);
		}
		if (bulkParkerAction === 'UNSUSPEND_PARKERS') {
			// end date, save button
			return (
				<>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label='End Date'
							disabled={isEndDateDisabled}
							value={endDate}
							onChange={(newValue: Dayjs | null) => {
								const year = newValue.year();
								if (year < 1900) {
									return; // Don't set the date if the year is invalid
								}
								setEndDate(newValue || null);
							}}
							views={['year', 'month', 'day']}
							format='MM/DD/YYYY'
							slotProps={{
								openPickerIcon: {
									'data-cy': 'date-picker-open-icon',
								},
								textField: {
									inputProps: {
										'data-cy': 'suspend-parker-end-date-date-picker',
									},
									sx: {
										height: '35px',
										boxSizing: 'border-box',
										'& .MuiInputBase-root': {
											height: '35px',
										},
										'& .MuiInputLabel-root': {
											marginTop: '-10px',
										},
										'& .MuiInputLabel-shrink': {
											marginTop: '0px',
										},
									},
								},
							}}
						/>
					</LocalizationProvider>
					<Button
						onClick={() => handleActionSubmitClick()}
						disabled={!rows?.length || !endDate || !selectedParkerIds.length}
						variant='contained'
						data-cy='save-button'
					>
						Save
					</Button>
				</>
			);
		}
		if (bulkParkerAction === 'CHANGE_PENDING_PARKER_START_DATE') {
			return (
				<>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label='New Start Date'
							value={newStartDate}
							onChange={(newValue: Dayjs | null) => {
								const year = newValue.year();
								if (year < 1900) {
									return; // Don't set the date if the year is invalid
								}
								setNewStartDate(newValue || null);
							}}
							views={['year', 'month', 'day']}
							format='MM/DD/YYYY'
							slotProps={{
								openPickerIcon: {
									'data-cy': 'date-picker-open-icon',
								},
								textField: {
									inputProps: {
										'data-cy': 'change-parker-start-date-date-picker',
									},
									sx: {
										height: '35px',
										boxSizing: 'border-box',
										'& .MuiInputBase-root': {
											height: '35px',
										},
										'& .MuiInputLabel-root': {
											marginTop: '-10px',
										},
										'& .MuiInputLabel-shrink': {
											marginTop: '0px',
										},
									},
								},
							}}
							minDate={dayjs()}
						/>
					</LocalizationProvider>
					<Button
						onClick={() => handleActionSubmitClick()}
						disabled={!rows?.length || !newStartDate || !selectedParkerIds.length}
						variant='contained'
						data-cy='save-button'
					>
						Save
					</Button>
				</>
			);
		}
		if (bulkParkerAction === 'REMOVE_ACCESS_SYSTEM_META_FOR_SELECTED_PARKERS') {
			return (
				<Button
					onClick={() => handleActionSubmitClick()}
					disabled={!rows?.length || !selectedParkerIds.length}
					variant='contained'
					data-cy='remove-access-system-meta-selected-button'
				>
					Remove Access System Meta
				</Button>
			);
		}
		if (bulkParkerAction === 'REMOVE_ACCESS_SYSTEM_META_ON_SUBSCRIPTION_AND_PARKERS') {
			return (
				<Button
					onClick={() => handleActionSubmitClick()}
					disabled={!rows?.length}
					variant='contained'
					data-cy='remove-access-system-meta-subscription-button'
				>
					Remove Access System Meta
				</Button>
			);
		}

		return null;
	}

	function handleAutoCompleteChange(_, newValue) {
		setBulkParkerAction(newValue?.value);
		const name = findLabelFromValueAndOptions(newValue?.value, bulkParkerActionOptions);
		setBulkParkerActionName(name);
		setIsEndDateDisabled(false);
		setEffectiveDate(null);
		setEndDate(null);
		if (!newValue) {
			setBulkParkerActionName(null);
			debouncedSetCacheFilters(defaultFiltersForCache);
			return;
		}
		let suspensionStatus = null;
		let terminationStatus = null;
		const notInvoiced = ParkerSuspensionStatusEnum.NOT_INVOICED.replace(/ /g, '_');
		const suspensionPending = ParkerSuspensionStatusEnum.SUSPENSION_PENDING.replace(/ /g, '_');
		const terminationPending = ParkerTerminationStatusEnum.TERMINATION_PENDING.replace(/ /g, '_');

		switch (newValue.value) {
			case 'ACTIVATE_ALL_PENDING_PARKERS':
			case 'ACTIVATE_PENDING_PARKERS':
			case 'CHANGE_PENDING_PARKER_START_DATE':
				suspensionStatus = [ParkerSuspensionStatusEnum.PENDING];
				terminationStatus = [ParkerTerminationStatusEnum.PENDING];
				break;
			case 'DELETE_PENDING_PARKERS':
			case 'DELETE_ALL_PENDING_PARKERS':
				suspensionStatus = [notInvoiced, ParkerSuspensionStatusEnum.PENDING];
				terminationStatus = [notInvoiced, ParkerTerminationStatusEnum.PENDING];
				break;
			case 'TERMINATE_ACTIVE_PARKERS':
				suspensionStatus = [
					ParkerSuspensionStatusEnum.ACTIVE,
					suspensionPending,
					ParkerSuspensionStatusEnum.SUSPENDED,
				];
				terminationStatus = [ParkerTerminationStatusEnum.ACTIVE];
				break;
			case 'SUSPEND_PARKERS':
				suspensionStatus = [ParkerSuspensionStatusEnum.ACTIVE];
				terminationStatus = [ParkerTerminationStatusEnum.ACTIVE, terminationPending];
				break;
			case 'UNSUSPEND_PARKERS':
				suspensionStatus = [ParkerSuspensionStatusEnum.SUSPENDED];
				terminationStatus = [ParkerTerminationStatusEnum.ACTIVE, terminationPending];
				break;
			case 'UPDATE_TERMINATION_PENDING_PARKERS':
				suspensionStatus = [
					ParkerSuspensionStatusEnum.ACTIVE,
					suspensionPending,
					ParkerSuspensionStatusEnum.SUSPENDED,
				];
				terminationStatus = [terminationPending];
				break;
			case 'UPDATE_SUSPENSION_PENDING_PARKERS':
				suspensionStatus = [suspensionPending];
				terminationStatus = [ParkerTerminationStatusEnum.ACTIVE, terminationPending];
				break;
			default:
				suspensionStatus = [ParkerSuspensionStatusEnum.ACTIVE, suspensionPending];
				terminationStatus = [ParkerTerminationStatusEnum.ACTIVE, terminationPending];
				break;
		}
		debouncedSetCacheFilters([
			{
				propertyName: 'terminationStatus',
				op: 'isAnyOf',
				value: terminationStatus,
			},
			{
				propertyName: 'suspensionStatus',
				op: 'isAnyOf',
				value: suspensionStatus,
			},
		]);
	}

	const syncAllParkersInAccessSystem = async () => {
		try {
			await syncAllParkers({
				variables: { subscriptionId: subIdToUse },
				context: {
					apiVersion: 'v2',
				},
			});
			setShowSnackbar({
				message:
					'Successfully queued up subscription parker sync. Please navigate to the Parker Access System History table to check for any failed updates',
				severity: 'success',
			});
		} catch {
			setShowSnackbar({
				message: 'There was an error syncing this subscription',
				severity: 'error',
			});
		}
	};

	const CustomToolbar = () => (
		<GridToolbarContainer sx={{ justifyContent: 'space-between' }}>
			<div>
				{!bulkParkerAction && (
					<GridToolbarFilterButton
						onResize={undefined}
						onResizeCapture={undefined}
						data-cy='filter-table-button'
					/>
				)}
				<GridToolbarColumnsButton onResize={undefined} onResizeCapture={undefined} />
				<GridToolbarDensitySelector onResize={undefined} onResizeCapture={undefined} />
				{csvExportButton ? (
					<CsvExportButtonV2
						query={GET_PARKERS_CSV}
						queryName='getParkersCSV'
						args={{
							variables: {
								limit: 10000,
								offset: 0,
								order: cacheSortOrder,
								filters: cacheFilters,
								facilityId: facilityIdToUse,
								subscriptionId: subIdToUse,
							},
							context: {
								apiVersion: 'v2',
								appName: process.env.NEXT_PUBLIC_APP_NAME,
							},
						}}
						setShowSnackbar={setShowSnackbar}
					/>
				) : null}
				{showLPRDownload ? (
					<CsvExportButtonV2
						query={GET_PARKERS_LPR_READINESS_CSV}
						queryName='getParkersLprReadinessCsv'
						buttonText='LPR Readiness Report'
						args={{
							variables: {
								filters: cacheFilters,
								facilityId: facilityIdToUse,
								subscriptionId: subIdToUse,
							},
							context: {
								apiVersion: 'v2',
								appName: process.env.NEXT_PUBLIC_APP_NAME,
							},
						}}
						setShowSnackbar={setShowSnackbar}
					/>
				) : null}
				{subIdToUse && !isOwnerPortal && (
					<Button
						startIcon={<AddIcon />}
						disabled={loading}
						onClick={() => {
							if (!parkerDetailModal) return;
							setShowDialogForParkersTable(
								parkerDetailModal({
									subscriptionId: subIdToUse,
									facilityId: facilityIdToUse,
									handleClose: () => setShowDialogForParkersTable(null),
									refetchParkersFromCache,
									refetchSubscriptionData,
								}),
							);
						}}
						data-cy='add-parker-button'
					>
						Add Parker
					</Button>
				)}
				{subIdToUse &&
					!isOwnerPortal &&
					!tenantFacilityId &&
					bulkUploadSubscriptionsAndParkersModal &&
					(!isAdminDashboard || hasComponentAccess('SubscriptionInfoParkersPageBulkUploadButton')) && (
						<Button
							disabled={facilityLoading}
							onClick={() => {
								setShowDialogForParkersTable(
									bulkUploadSubscriptionsAndParkersModal({
										parkersOnly: true,
										facilityFromParkersTable: {
											id: facilityIdToUse,
											facilityNumber: facilityData.getFacility.facilityNumber,
											facilityName: facilityData.getFacility.facilityName,
											organizationId: facilityData.getFacility.organizationId,
											integration: facilityData.getFacility.integration,
											usesDefaultRates:
												facilityData.getFacility.facilitySettings.usesDefaultRates,
											maxActiveVehicles:
												facilityData.getFacility.facilitySettings.maxActiveVehicles,
											barcodeRequired:
												facilityData.getFacility.accessSystemCredentials?.barcodeRequired,
											minActiveVehicles:
												facilityData.getFacility.facilitySettings.minActiveVehicles,
										},
										onClose: () => setShowDialogForParkersTable(null),
									}),
								);
							}}
							data-cy='bulk-upload-parkers-datagrid-button'
						>
							Bulk Upload Parkers
						</Button>
					)}
				{subIdToUse &&
					!isOwnerPortal &&
					!tenantFacilityId &&
					(!isAdminDashboard || hasComponentAccess('SubscriptionInfoParkersPageAddBarcodesButton')) && (
						<Button
							disabled={loadingAddBarcodesToParkers}
							onClick={() =>
								setShowDialog(
									<ConfirmationInputModal
										title='Click the button below to add barcodes to all of the parkers.'
										validationString='CONFIRM'
										dismissText='Cancel'
										confirmText='Add Barcodes'
										handleDismiss={() => setShowDialog(null)}
										handleConfirm={() => {
											addBarcodesToParkersForSubscription({
												variables: {
													subscriptionId: subIdToUse,
												},
												context: {
													apiVersion: 'v2',
													appName: process.env.NEXT_PUBLIC_APP_NAME,
												},
											});

											setShowDialog(null);
										}}
									/>,
								)
							}
							data-cy='bulk-upload-parkers-datagrid-button'
						>
							Add Barcodes For All Parkers
						</Button>
					)}
				{
					// eslint-disable-next-line @stylistic/max-len
					subIdToUse &&
						!isOwnerPortal &&
						!tenantFacilityId &&
						facilityData?.getFacility?.integration !== 'NONE' && (
							<Button disabled={loadingAddBarcodesToParkers} onClick={syncAllParkersInAccessSystem}>
								Sync All Parkers In Access System
							</Button>
						)
				}
				{subIdToUse && !isOwnerPortal && (
					<div
						style={{
							width: '100%',
							display: 'flex',
							alignItems: 'center',
							flexWrap: 'wrap',
							justifyContent: 'flex-start',
							flexGrow: 1,
							marginTop: '12px',
						}}
					>
						<Autocomplete
							id='facility-select'
							style={{ marginRight: '10px' }}
							options={bulkParkerActionOptions}
							key={bulkParkerActionName}
							value={bulkParkerActionName as any}
							isOptionEqualToValue={(option, value) => option.label === value}
							renderInput={(params) => (
								<TextField
									{...params}
									label='Action'
									data-cy='parker-bulk-actions-autocomplete'
									InputProps={{
										...params.InputProps,
										style: { height: '35px', width: '400px', padding: '0px 10px' },
									}}
									InputLabelProps={{
										style: {
											transform: bulkParkerActionName
												? 'translate(14px, -6px) scale(0.75)'
												: 'translate(14px, 10px) scale(1)',
											fontSize: '16px',
										},
										shrink: Boolean(bulkParkerActionName),
									}}
								/>
							)}
							onChange={handleAutoCompleteChange}
						/>
						{renderCorrectInputsFromAction()}
					</div>
				)}
			</div>
			<div style={{ display: 'flex', flexDirection: 'row' }}>
				{typeof BulkRateCSVComponent === 'function'
					? BulkRateCSVComponent({
							query: LIST_PARKERS_WITH_MONTHLY_FEE_ID,
							order: cacheSortOrder,
							filters: [...defaultFiltersForCache, ...(cacheFilters ?? [])],
						})
					: BulkRateCSVComponent}
				{BulkFeeChangeComponent}
			</div>
		</GridToolbarContainer>
	);

	return (
		<div style={{ display: 'flex', flexDirection: 'column' }}>
			{tabs}
			{subIdToUse && (
				<Grid container direction='row' alignItems='start'>
					{!!leaseInvoiceMonths.length && !bulkParkerActionName && (
						<Button
							variant='contained'
							sx={{ mt: 1 }}
							onClick={() =>
								setShowDialogForParkersTable(
									<LeaseInvoiceMonthsModal
										leaseInvoiceMonths={leaseInvoiceMonths}
										doCreateLeasesFirstMonthInvoices={doCreateLeasesFirstMonthInvoices}
										setShowDialog={setShowDialogForParkersTable}
										theme={theme || OwnerPortalTheme}
									/>,
								)
							}
						>
							Create First Month Invoice(s)
						</Button>
					)}
				</Grid>
			)}
			{showPendingParkersAlert && (
				<Alert severity='info' sx={{ mb: 2, color: 'black' }}>
					This Subscription currently has Pending Parkers. Please ensure all desired Parkers are Activated.
					Pending Parkers will not be billed.
				</Alert>
			)}
			<div className='pca__datagrid-sticky--headfoot' data-cy='parkers-table-container'>
				<DataGridPremium
					checkboxSelection={
						!!bulkParkerActionName &&
						bulkParkerActionName !== BulkParkerActionEnum.ACTIVATE_ALL_PENDING_PARKERS &&
						bulkParkerActionName !== BulkParkerActionEnum.DELETE_ALL_PENDING_PARKERS &&
						bulkParkerActionName !==
							BulkParkerActionEnum.REMOVE_ACCESS_SYSTEM_META_ON_SUBSCRIPTION_AND_PARKERS
					}
					paginationModel={{ page, pageSize }}
					onSortModelChange={(sortModel) => {
						setInitialLoadFlag(false);
						setLoading(true);
						onSortModelChangeForCache(sortModel, setPaginationModel, setCacheSortOrder);
					}}
					onFilterModelChange={(filterModel) => {
						setInitialLoadFlag(false);
						setLoading(true);
						onFilterModelChangeForCache(filterModel, setPaginationModel, debouncedSetCacheFilters);
					}}
					hideFooter={parkerCacheLoading}
					hideFooterPagination={parkerCacheLoading}
					onPaginationModelChange={(newModel) => {
						setInitialLoadFlag(false);
						setLoading(true);
						setPaginationModel(newModel);
					}}
					pagination
					rows={rows || []}
					rowCount={rowCount}
					pageSizeOptions={[pageSize]}
					columns={columns}
					paginationMode='server'
					sortingMode='server'
					filterMode='server'
					loading={loading}
					slots={{
						toolbar: CustomToolbar,
						loadingOverlay: DatagridLinearProgress,
					}}
					slotProps={{
						filterPanel: { ...datagridFilterPanelAttributes },
					}}
					rowSelectionModel={selectedParkerIds}
					onRowSelectionModelChange={(ids) => setSelectedParkerIds(ids)}
					disableColumnFilter={!!bulkParkerAction}
					initialState={{
						filter: {
							filterModel: {
								items: [
									{
										id: 1,
										field: 'suspensionStatus',
										operator: 'isAnyOf',
										value: ['Active', 'Not_Invoiced', 'Suspension_Pending'],
									},
									{
										id: 2,
										field: 'terminationStatus',
										operator: 'isAnyOf',
										value: ['Active', 'Not_Invoiced', 'Termination_Pending'],
									},
								],
							},
						},
						columns: {
							columnVisibilityModel: {
								residentUnit: false,
								tenantName: false,
							},
						},
					}}
					getRowClassName={(params) => (parkersWithErrors.includes(params.id) ? 'red-row' : '')}
					sx={{
						'& .red-row': {
							backgroundColor: 'rgba(255, 99, 71, .9) !important',
						},
						height: '100%',
					}}
				/>
				{!showSnackbar && (
					<Snackbar
						showSnackbar={showSnackbarForParkersTable}
						setShowSnackbar={setShowSnackbarForParkersTable}
					/>
				)}
				{!showDialog && (
					<Dialog showDialog={showDialogForParkersTable} setShowDialog={setShowDialogForParkersTable} />
				)}
			</div>
		</div>
	);
};
